import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Upload, FileText, Check, AlertTriangle, Loader2, Shield,
  Trash2, ChevronRight, Info, X, File, Image, Plus,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const RISK_CONFIG = {
  leicht: { color: "border-l-emerald-500", label: "Leichter Fall", badge: "default" },
  mittel: { color: "border-l-amber-500", label: "Mittlerer Fall", badge: "secondary" },
  schwer: { color: "border-l-red-500", label: "Schwerer Fall", badge: "destructive" },
};

const FIELD_LABELS = {
  delikt_art: "Art des Delikts",
  delikt_datum: "Datum des Vorfalls",
  bak_wert: "BAK-Wert (Promille)",
  substanz: "Substanz",
  wiederholungstat: "Wiederholungstat",
  fragestellung_gutachter: "Fragestellung des Gutachters",
  auflagen: "Auflagen",
  zusammenfassung: "Zusammenfassung",
};

const FIELD_WARNINGS = {
  bak_wert: "OCR kann Zahlen verwechseln (z.B. 1,8 und 1,3). Bitte genau pruefen.",
  delikt_datum: "Bitte Datum ueberpruefen — Ziffern koennen verwechselt worden sein.",
  fragestellung_gutachter: "Dieses Feld ist besonders wichtig. Bitte pruefen Sie den vollstaendigen Wortlaut.",
  auflagen: "Bei mehreren Auflagen: Sind alle erfasst?",
};

const ACCEPTED_FORMATS = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
};
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 25 * 1024 * 1024;

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function FileIcon({ type }) {
  if (type === "application/pdf") return <FileText className="w-5 h-5 text-red-500" />;
  if (type?.startsWith("image/")) return <Image className="w-5 h-5 text-blue-500" />;
  return <File className="w-5 h-5 text-muted-foreground" />;
}

// ── File Preview Item ──
function FilePreviewItem({ file, onRemove }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg group" data-testid={`file-preview-${file.name}`}>
      <FileIcon type={file.type} />
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm font-medium truncate">{file.name}</p>
        <p className="font-body text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
      </div>
      <button
        onClick={() => onRemove(file)}
        className="p-1 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
        data-testid={`remove-file-${file.name}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Processing Animation ──
function ProcessingStatus({ step, filesProcessed, filesTotal, progress }) {
  return (
    <div className="text-center py-10 space-y-6" data-testid="akte-processing">
      <div className="relative w-16 h-16 mx-auto">
        <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
        <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <FileText className="w-6 h-6 text-primary" />
        </div>
      </div>
      <div>
        <h2 className="font-heading text-xl font-bold mb-2">
          {step || "Dokumente werden analysiert..."}
        </h2>
        {filesTotal > 1 && filesProcessed < filesTotal && (
          <p className="font-body text-sm text-muted-foreground">
            Datei {Math.min(filesProcessed + 1, filesTotal)} von {filesTotal}
          </p>
        )}
      </div>
      <div className="max-w-xs mx-auto">
        <Progress value={progress} className="h-2" data-testid="processing-progress" />
      </div>
      <p className="font-body text-xs text-muted-foreground">
        Dies kann bis zu einer Minute dauern.
      </p>
    </div>
  );
}

export default function AkteUploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Steps: info → consent → upload → processing → confirm → done
  const [step, setStep] = useState("info");
  const [consent, setConsent] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingData, setProcessingData] = useState({ step: "", filesProcessed: 0, filesTotal: 0 });
  const [extracted, setExtracted] = useState(null);
  const [akteId, setAkteId] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [risk, setRisk] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [editData, setEditData] = useState({});
  const [existingAkte, setExistingAkte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [extractionQuality, setExtractionQuality] = useState("good");
  const [fileResults, setFileResults] = useState([]);
  const pollingRef = useRef(null);

  // ── Load existing akte on mount ──
  useEffect(() => {
    Promise.all([
      api.get("/akte/current").catch(() => ({ data: { has_akte: false } })),
      api.get("/participant/connection").catch(() => ({ data: { connected: false } })),
    ]).then(([akteRes, connRes]) => {
      const akte = akteRes.data;
      if (akte.has_akte) {
        if (akte.status === "processing") {
          // Resume polling
          setUploadId(akte.upload_id);
          setAkteId(akte.akte_id);
          setProcessingData({
            step: akte.processing_step || "Wird verarbeitet...",
            filesProcessed: akte.files_processed || 0,
            filesTotal: akte.files_total || 1,
          });
          setStep("processing");
        } else if (akte.status === "extracted") {
          // Show confirm
          setAkteId(akte.akte_id);
          setUploadId(akte.upload_id);
          setExtracted({
            delikt_art: akte.delikt_art,
            delikt_datum: akte.delikt_datum,
            bak_wert: akte.bak_wert,
            substanz: akte.substanz,
            wiederholungstat: akte.wiederholungstat,
            fragestellung_gutachter: akte.fragestellung_gutachter,
            auflagen: akte.auflagen,
            zusammenfassung: akte.zusammenfassung,
          });
          setEditData({
            delikt_art: akte.delikt_art,
            delikt_datum: akte.delikt_datum,
            bak_wert: akte.bak_wert,
            substanz: akte.substanz,
            wiederholungstat: akte.wiederholungstat,
            fragestellung_gutachter: akte.fragestellung_gutachter,
            auflagen: akte.auflagen,
            zusammenfassung: akte.zusammenfassung,
          });
          setRisk({ risiko_level: akte.risiko_level, risiko_faktoren: akte.risiko_faktoren });
          setExtractionQuality(akte.extraction_quality || "good");
          setFileResults(akte.file_results || []);
          setStep("confirm");
        } else if (akte.status === "confirmed") {
          setExistingAkte(akte);
        }
      }
      setConnection(connRes.data);
    }).finally(() => setLoading(false));
  }, []);

  // ── Polling for processing status ──
  useEffect(() => {
    if (step !== "processing" || !uploadId) return;

    const poll = async () => {
      try {
        const res = await api.get(`/akte/status/${uploadId}`);
        const akte = res.data;

        setProcessingData({
          step: akte.processing_step || "Wird verarbeitet...",
          filesProcessed: akte.files_processed || 0,
          filesTotal: akte.files_total || 1,
        });

        if (akte.status === "extracted") {
          clearInterval(pollingRef.current);
          setAkteId(akte.akte_id);
          const fields = {
            delikt_art: akte.delikt_art,
            delikt_datum: akte.delikt_datum,
            bak_wert: akte.bak_wert,
            substanz: akte.substanz,
            wiederholungstat: akte.wiederholungstat,
            fragestellung_gutachter: akte.fragestellung_gutachter,
            auflagen: akte.auflagen,
            zusammenfassung: akte.zusammenfassung,
          };
          setExtracted(fields);
          setEditData(fields);
          setRisk({ risiko_level: akte.risiko_level, risiko_faktoren: akte.risiko_faktoren });
          setExtractionQuality(akte.extraction_quality || "good");
          setFileResults(akte.file_results || []);

          if (akte.extraction_quality === "failed") {
            toast.info("Dokumente konnten nicht gelesen werden. Bitte Felder manuell ausfuellen.");
          } else if (akte.extraction_quality === "partial") {
            toast.warning("Dokumente teilweise lesbar — bitte Ergebnisse genau pruefen.");
          }
          setStep("confirm");
        } else if (akte.status === "error") {
          clearInterval(pollingRef.current);
          toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
          setStep("upload");
        }
      } catch {
        // Polling error — continue
      }
    };

    poll();
    pollingRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollingRef.current);
  }, [step, uploadId]);

  // ── Dropzone ──
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    rejectedFiles.forEach((r) => {
      const err = r.errors[0];
      if (err?.code === "file-too-large") {
        toast.error(`"${r.file.name}" ist zu gross (max. 10 MB)`);
      } else if (err?.code === "file-invalid-type") {
        toast.error(`"${r.file.name}": Format nicht unterstuetzt`);
      } else {
        toast.error(`"${r.file.name}": ${err?.message || "Ungueltiges Format"}`);
      }
    });

    setSelectedFiles((prev) => {
      const combined = [...prev, ...acceptedFiles];
      if (combined.length > MAX_FILES) {
        toast.error(`Maximal ${MAX_FILES} Dateien`);
        return prev;
      }
      const totalSize = combined.reduce((s, f) => s + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        toast.error("Gesamtgroesse ueberschreitet 25 MB");
        return prev;
      }
      return combined;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FORMATS,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    disabled: uploading,
  });

  const removeFile = (file) => {
    setSelectedFiles((prev) => prev.filter((f) => f !== file));
  };

  // ── Upload handler ──
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      selectedFiles.forEach((f) => formData.append("files", f));
      formData.append("consent", "true");

      const res = await api.post("/akte/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });

      setUploadId(res.data.upload_id);
      setAkteId(res.data.akte_id);
      setProcessingData({
        step: "Dateien werden vorbereitet...",
        filesProcessed: 0,
        filesTotal: res.data.files_total,
      });
      setStep("processing");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen. Bitte versuchen Sie es erneut.");
      setUploadProgress(0);
    }
    setUploading(false);
  };

  // ── Confirm handler ──
  const handleConfirm = async () => {
    if (!akteId) return;
    setConfirming(true);
    try {
      const res = await api.put(`/akte/${akteId}/confirm`, editData);
      setRisk(res.data.risiko);
      setStep("done");
      if (connection?.connected) setShowShareDialog(true);
      toast.success("Akte bestaetigt — Ihr Training wird jetzt angepasst");
    } catch {
      toast.error("Fehler beim Bestaetigen");
    }
    setConfirming(false);
  };

  const handleShareAkte = async () => {
    setSharing(true);
    try {
      await api.post("/participant/share-akte");
      toast.success("Akte-Zusammenfassung fuer Ihre Beratungsstelle freigegeben");
    } catch {
      toast.error("Freigabe fehlgeschlagen");
    }
    setSharing(false);
    setShowShareDialog(false);
  };

  // Calculate processing progress
  const calcProgress = () => {
    const { filesProcessed, filesTotal } = processingData;
    if (!filesTotal) return 30;
    const fileProgress = (filesProcessed / filesTotal) * 80;
    return Math.min(Math.round(fileProgress + 10), 95);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Existing Akte → Management View ──
  if (existingAkte && step === "info") {
    const riskCfg = RISK_CONFIG[existingAkte.risiko_level] || RISK_CONFIG.leicht;
    return (
      <div className="page-transition max-w-2xl mx-auto space-y-6 px-4" data-testid="akte-management">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Meine MPU-Akte</h1>
        <Card className="border-border/50" data-testid="akte-summary-card">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                <span className="font-body text-sm font-medium">{existingAkte.delikt_art || "Akte vorhanden"}</span>
              </div>
              <Badge variant={riskCfg.badge}>{riskCfg.label}</Badge>
            </div>
            {existingAkte.bak_wert && (
              <p className="font-body text-sm text-muted-foreground">BAK: {existingAkte.bak_wert}</p>
            )}
            {existingAkte.fragestellung_gutachter && (
              <div className="bg-primary/5 rounded-lg p-3">
                <p className="font-body text-xs text-primary font-medium mb-1">Fragestellung des Gutachters:</p>
                <p className="font-body text-sm italic">"{existingAkte.fragestellung_gutachter}"</p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="font-body text-xs gap-1"
                onClick={() => { setExistingAkte(null); setStep("info"); }}
                data-testid="new-akte-btn"
              >
                <Upload className="w-3 h-3" /> Neue Akte hochladen
              </Button>
            </div>
          </CardContent>
        </Card>
        <Button variant="outline" className="font-body w-full" onClick={() => navigate(-1)}>
          Zurueck
        </Button>
      </div>
    );
  }

  return (
    <div className="page-transition max-w-2xl mx-auto space-y-6 px-4" data-testid="akte-upload-page">

      {/* ═══ STEP: INFO ═══ */}
      {step === "info" && (
        <>
          <div className="text-center pt-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Ihre MPU-Akte hochladen</h1>
            <p className="font-body text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Laden Sie das offizielle Schreiben der Fuehrerscheinbehoerde hoch.
              Die KI trainiert dann gezielt auf Ihre Situation und die Fragestellung Ihres Gutachters.
            </p>
          </div>

          <Card className="border-border/50">
            <CardContent className="pt-6 space-y-3">
              <p className="font-body text-xs font-medium text-primary">Was wird analysiert?</p>
              {["Art des Delikts und Datum", "BAK-Wert (bei Alkohol)", "Fragestellung des MPU-Gutachters", "Auflagen und Sperrfristen"].map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="font-body text-sm">{t}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2 text-muted-foreground">
                <Shield className="w-3.5 h-3.5" />
                <span className="font-body text-xs">Originale werden nach der Analyse geloescht — nur die Textdaten bleiben.</span>
              </div>
            </CardContent>
          </Card>

          {/* Tipp-Box */}
          <Card className="border-border/50 bg-primary/5 border-l-4 border-l-primary">
            <CardContent className="pt-4 pb-3">
              <p className="font-body text-xs font-medium flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-primary" /> Tipp
              </p>
              <p className="font-body text-xs text-muted-foreground mt-1">
                Das Anordnungsschreiben der Behoerde enthaelt alle wichtigen Informationen — besonders die Fragestellung des Gutachters.
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/50 bg-muted/20">
            <CardContent className="pt-4 pb-3">
              <p className="font-body text-xs font-medium mb-2">Akzeptierte Formate</p>
              <div className="flex flex-wrap gap-2">
                {["PDF", "JPG", "PNG", "HEIC"].map((fmt) => (
                  <span key={fmt} className="font-body text-xs bg-background px-2 py-1 rounded border border-border">{fmt}</span>
                ))}
              </div>
              <p className="font-body text-xs text-muted-foreground mt-2">Bis zu 5 Dateien, max. 10 MB pro Datei</p>
            </CardContent>
          </Card>

          <Button
            className="w-full font-body bg-primary text-primary-foreground"
            onClick={() => setStep("consent")}
            data-testid="akte-start-btn"
          >
            Weiter zur Einwilligung <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <div className="text-center">
            <button
              className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
              onClick={() => navigate(-1)}
              data-testid="skip-akte-btn"
            >
              Ueberspringen — ich moechte ohne Akte trainieren
            </button>
          </div>
        </>
      )}

      {/* ═══ STEP: CONSENT ═══ */}
      {step === "consent" && (
        <>
          <h2 className="font-heading text-xl font-bold">Einwilligung zur Analyse</h2>
          <Card className="border-border/50">
            <CardContent className="pt-6 space-y-4">
              <div className="bg-muted/30 rounded-lg p-4 font-body text-sm leading-relaxed" data-testid="consent-text">
                <p className="font-medium mb-2">Einwilligung zur Analyse Ihrer MPU-Dokumente</p>
                <p className="mb-2">
                  Ich willige ein, dass Spurwechsel PRO meine hochgeladenen MPU-Dokumente (bis zu 5 Dateien) analysiert und folgende Informationen extrahiert:
                </p>
                <ul className="list-disc pl-5 space-y-1 mb-2">
                  <li>Art des Delikts und Datum</li>
                  <li>Blutalkoholkonzentration (BAK-Wert) falls zutreffend</li>
                  <li>Fragestellung des MPU-Gutachters</li>
                  <li>Auflagen und Sperrfristen</li>
                </ul>
                <p className="mb-2">
                  Diese Informationen werden ausschliesslich zur Verbesserung meines persoenlichen MPU-Trainings verwendet.
                  Alle Original-Dokumente werden unmittelbar nach der Analyse vollstaendig und unwiderruflich geloescht — es werden nur die extrahierten Textinformationen gespeichert.
                </p>
                <p className="mb-2">
                  Ich kann diese Einwilligung jederzeit widerrufen. Bei Widerruf werden alle gespeicherten Akte-Daten sofort und vollstaendig geloescht.
                </p>
                <p className="text-xs text-muted-foreground">Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                  data-testid="akte-consent-checkbox"
                />
                <span className="font-body text-sm">
                  Ich habe die Informationen gelesen und stimme der Analyse meiner Dokumente zu.
                </span>
              </label>
              <p className="font-body text-xs text-muted-foreground flex items-center gap-1">
                <Info className="w-3 h-3 flex-shrink-0" />
                Bitte laden Sie nur Dokumente hoch, die Sie selbst betreffen.
              </p>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" className="font-body" onClick={() => setStep("info")} data-testid="consent-back-btn">
              Zurueck
            </Button>
            <Button
              className="flex-1 font-body bg-primary text-primary-foreground"
              disabled={!consent}
              onClick={() => setStep("upload")}
              data-testid="consent-next-btn"
            >
              Weiter zum Upload <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* "Ich habe keine Akte" */}
          <div className="text-center">
            <button
              className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
              onClick={() => navigate("/akte?manual=true")}
              data-testid="no-akte-btn"
            >
              Ich habe meine Akte nicht zur Hand → Manuelle Eingabe
            </button>
          </div>
        </>
      )}

      {/* ═══ STEP: UPLOAD ═══ */}
      {step === "upload" && (
        <>
          <h2 className="font-heading text-xl font-bold">Dokumente hochladen</h2>
          <p className="font-body text-sm text-muted-foreground -mt-4">
            Laden Sie das Anordnungsschreiben und weitere relevante Dokumente hoch.
          </p>

          {/* Drag & Drop Zone */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
              ${isDragActive ? "border-primary bg-primary/5 scale-[1.01]" : "border-border hover:border-primary/50 hover:bg-muted/20"}
              ${uploading ? "opacity-50 pointer-events-none" : ""}
            `}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="akte-file-input" />
            <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragActive ? "text-primary" : "text-muted-foreground"}`} />
            {isDragActive ? (
              <p className="font-body text-sm text-primary font-medium">Dateien hier ablegen...</p>
            ) : (
              <>
                <p className="font-body text-sm font-medium">Dateien hierher ziehen</p>
                <p className="font-body text-xs text-muted-foreground mt-1">oder klicken zum Auswaehlen</p>
                <p className="font-body text-xs text-muted-foreground mt-2">PDF, JPG, PNG, HEIC — max. 10 MB pro Datei</p>
              </>
            )}
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2" data-testid="file-list">
              <div className="flex items-center justify-between">
                <p className="font-body text-xs font-medium text-muted-foreground">
                  {selectedFiles.length} {selectedFiles.length === 1 ? "Datei" : "Dateien"} ausgewaehlt
                  ({formatFileSize(selectedFiles.reduce((s, f) => s + f.size, 0))})
                </p>
                {selectedFiles.length < MAX_FILES && (
                  <button
                    className="font-body text-xs text-primary flex items-center gap-1 hover:underline"
                    onClick={() => document.querySelector('[data-testid="akte-file-input"]')?.click()}
                    data-testid="add-more-files-btn"
                  >
                    <Plus className="w-3 h-3" /> Weitere
                  </button>
                )}
              </div>
              {selectedFiles.map((file, i) => (
                <FilePreviewItem key={`${file.name}-${i}`} file={file} onRemove={removeFile} />
              ))}
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2" data-testid="upload-progress">
              <div className="flex items-center justify-between">
                <p className="font-body text-xs font-medium">Hochladen...</p>
                <span className="font-body text-xs text-muted-foreground">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="font-body"
              onClick={() => { setSelectedFiles([]); setStep("consent"); }}
              disabled={uploading}
              data-testid="upload-back-btn"
            >
              Zurueck
            </Button>
            <Button
              className="flex-1 font-body bg-primary text-primary-foreground gap-2"
              disabled={selectedFiles.length === 0 || uploading}
              onClick={handleUpload}
              data-testid="upload-submit-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Wird hochgeladen...</>
              ) : (
                <><Upload className="w-4 h-4" /> Hochladen und analysieren</>
              )}
            </Button>
          </div>
        </>
      )}

      {/* ═══ STEP: PROCESSING ═══ */}
      {step === "processing" && (
        <ProcessingStatus
          step={processingData.step}
          filesProcessed={processingData.filesProcessed}
          filesTotal={processingData.filesTotal}
          progress={calcProgress()}
        />
      )}

      {/* ═══ STEP: CONFIRM ═══ */}
      {step === "confirm" && extracted && (
        <>
          <h2 className="font-heading text-xl font-bold">Bitte pruefen Sie die Ergebnisse</h2>

          {/* File Results Summary */}
          {fileResults.length > 0 && (
            <Card className="border-border/50">
              <CardContent className="pt-4 pb-3">
                <p className="font-body text-xs font-medium mb-2">
                  {fileResults.filter((r) => r.status === "success").length} von {fileResults.length} Dateien erfolgreich analysiert
                </p>
                <div className="space-y-1">
                  {fileResults.map((fr, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {fr.status === "success" ? (
                        <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <X className="w-3 h-3 text-red-500 flex-shrink-0" />
                      )}
                      <span className="font-body text-xs truncate">{fr.filename}</span>
                      {fr.status === "failed" && (
                        <span className="font-body text-xs text-red-500">— nicht lesbar</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {extractionQuality === "partial" && (
            <Card className="border-l-4 border-l-amber-500" data-testid="quality-warning">
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <p className="font-body text-sm font-medium">Dokumente nur teilweise lesbar</p>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-1">
                  Einige Felder konnten moeglicherweise nicht korrekt erfasst werden. Bitte pruefen Sie alle Angaben besonders sorgfaeltig.
                </p>
              </CardContent>
            </Card>
          )}

          {extractionQuality === "failed" && (
            <Card className="border-l-4 border-l-red-500" data-testid="quality-failed">
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="font-body text-sm font-medium">Dokumente konnten nicht gelesen werden</p>
                </div>
                <p className="font-body text-xs text-muted-foreground mt-1">
                  Bitte fuellen Sie die Felder manuell aus oder laden Sie besser lesbare Dokumente hoch.
                </p>
              </CardContent>
            </Card>
          )}

          {risk && (
            <Card className={`border-l-4 ${RISK_CONFIG[risk.risiko_level]?.color || "border-l-emerald-500"}`} data-testid="risk-card">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={RISK_CONFIG[risk.risiko_level]?.badge}>{RISK_CONFIG[risk.risiko_level]?.label}</Badge>
                </div>
                {risk.risiko_faktoren?.length > 0 && (
                  <p className="font-body text-xs text-muted-foreground">{risk.risiko_faktoren.join(" · ")}</p>
                )}
                <p className="font-body text-[10px] text-muted-foreground mt-2 italic">
                  Diese Einschaetzung dient als grobe Orientierung und ist keine professionelle Fallbeurteilung.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="space-y-4" data-testid="akte-confirm-form">
            {Object.entries(FIELD_LABELS).map(([key, label]) => {
              const warning = FIELD_WARNINGS[key];
              return (
                <div key={key}>
                  <div className="mb-1">
                    <Label className="font-body text-sm font-medium">{label}</Label>
                    {warning && (
                      <div className="flex items-center gap-1 mt-0.5 text-amber-500" data-testid={`field-warning-${key}`}>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span className="font-body text-[10px]">{warning}</span>
                      </div>
                    )}
                    {!warning && (
                      <div className="flex items-center gap-1 mt-0.5 text-muted-foreground" data-testid={`field-warning-${key}`}>
                        <Info className="w-3 h-3 flex-shrink-0" />
                        <span className="font-body text-[10px]">Bitte pruefen</span>
                      </div>
                    )}
                  </div>
                  {key === "wiederholungstat" ? (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!editData[key]}
                        onCheckedChange={(v) => setEditData((d) => ({ ...d, [key]: v }))}
                        data-testid={`field-${key}`}
                      />
                      <span className="font-body text-sm">{editData[key] ? "Ja" : "Nein"}</span>
                    </div>
                  ) : key === "auflagen" ? (
                    <Input
                      value={Array.isArray(editData[key]) ? editData[key].join(", ") : editData[key] || ""}
                      onChange={(e) => setEditData((d) => ({
                        ...d,
                        [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                      }))}
                      className="font-body text-sm"
                      placeholder="Kommagetrennt"
                      data-testid={`field-${key}`}
                    />
                  ) : key === "bak_wert" ? (
                    <Input
                      type="number"
                      step="0.1"
                      value={editData[key] ?? ""}
                      onChange={(e) => setEditData((d) => ({
                        ...d,
                        [key]: e.target.value ? parseFloat(e.target.value) : null,
                      }))}
                      className="font-body text-sm"
                      placeholder="z.B. 1.8"
                      data-testid={`field-${key}`}
                    />
                  ) : (
                    <Input
                      value={editData[key] || ""}
                      onChange={(e) => setEditData((d) => ({ ...d, [key]: e.target.value }))}
                      className="font-body text-sm"
                      data-testid={`field-${key}`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <Button
            className="w-full font-body bg-primary text-primary-foreground gap-2"
            onClick={handleConfirm}
            disabled={confirming}
            data-testid="akte-confirm-btn"
          >
            {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Daten bestaetigen und Training anpassen
          </Button>
        </>
      )}

      {/* ═══ STEP: DONE ═══ */}
      {step === "done" && (
        <div className="text-center py-8" data-testid="akte-done">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-emerald-500" />
          </div>
          <h2 className="font-heading text-xl font-bold mb-2">Akte analysiert</h2>
          <p className="font-body text-sm text-muted-foreground mb-6">
            Ihr Training wird jetzt noch gezielter auf Ihre Situation abgestimmt.
          </p>
          <Button
            className="font-body bg-primary text-primary-foreground gap-2"
            onClick={() => navigate("/lernpfad")}
            data-testid="akte-to-training-btn"
          >
            Zum Lernpfad <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ── Akte Share Dialog ── */}
      {showShareDialog && (
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Akte mit Beratungsstelle teilen?</DialogTitle>
              <DialogDescription className="font-body text-sm">
                Sie sind mit <strong>{connection?.stelle_name}</strong> verbunden.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2" data-testid="share-akte-dialog">
              <p className="font-body text-sm">Ihre Beratungsstelle kann folgendes einsehen, wenn Sie zustimmen:</p>
              <ul className="font-body text-sm text-muted-foreground space-y-1 list-disc pl-5">
                <li>Art des Delikts und Datum</li>
                <li>BAK-Wert</li>
                <li>Auflagen und Risiko-Einschaetzung</li>
                <li>Fragestellung des Gutachters</li>
              </ul>
              <p className="font-body text-xs text-muted-foreground">
                Sie koennen diese Freigabe jederzeit in Ihrem Profil widerrufen.
              </p>
            </div>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowShareDialog(false)} className="font-body" data-testid="share-akte-decline-btn">
                Nein, nicht teilen
              </Button>
              <Button onClick={handleShareAkte} disabled={sharing} className="font-body bg-primary text-primary-foreground gap-2" data-testid="share-akte-confirm-btn">
                {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Ja, Akte freigeben
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
